import { Button, Grid, Box, Chip, Typography, FormControl, FormControlLabel, Radio, RadioGroup, Checkbox, FormGroup, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import AppModal from "components/AppModal";
import { addWeeks, isBefore } from "date-fns";
import { t } from "i18next";
import { FC, useState, useEffect } from "react";
import { shiftsReport } from "./shiftsReport";
import { H4 } from "components/Typography";
import { Download, ExpandMore } from "@mui/icons-material";
import { useSeason } from "contexts/SeasonContext";
import { styled } from "@mui/material/styles";
import { departmentsApi } from "api";
import { checkTokenRole } from "utils/checkToken";
import { DepartmentEntity } from "api/generated";

const BetaChip = styled(Chip)(({ theme }) => ({
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    fontWeight: "bold",
    fontSize: "0.75rem",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
    "& .MuiChip-label": {
        padding: "4px 12px",
    },
}))

interface ModalProps {
    open: boolean;
    onClose: () => void;
    currentDepartment?: { id: number; label: string } | null;
}

const ShiftReportModal: FC<ModalProps> = ({ open, onClose, currentDepartment }) => {
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(addWeeks(startDate, 2));
    const { seasonId } = useSeason();
    
    // Department selection state
    const [departmentScope, setDepartmentScope] = useState<'single' | 'all' | 'specific'>('single');
    const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
    
    // Work position selection state
    const [selectedWorkPositions, setSelectedWorkPositions] = useState<string[]>([]);
    
    // Departments state
    const [departments, setDepartments] = useState<DepartmentEntity[]>([]);

    // Mock work positions data - replace with actual API call
    const workPositions = [
        { id: '1', name: 'Chef' },
        { id: '2', name: 'Sous Chef' },
        { id: '3', name: 'Waiter' },
        { id: '4', name: 'Bartender' },
        { id: '5', name: 'Housekeeper' },
        { id: '6', name: 'Maintenance Worker' },
        { id: '7', name: 'Manager' },
        { id: '8', name: 'Assistant Manager' },
    ];

    const fetchDepartments = async () => {
        try {
            const role = checkTokenRole();
            const d = role === "DEPCHIEF"
                ? (await departmentsApi.findWhereIsBoss(seasonId)).data
                : (await departmentsApi.findAll()).data;
            setDepartments(d);
        } catch (error) {
            console.error('Error fetching departments:', error);
        }
    };

    useEffect(() => {
        if (open && seasonId) {
            fetchDepartments();
        }
    }, [open, seasonId]);

    const handleDepartmentToggle = (departmentId: string) => {
        setSelectedDepartments(prev => 
            prev.includes(departmentId) 
                ? prev.filter(id => id !== departmentId)
                : [...prev, departmentId]
        );
    };

    const handleWorkPositionToggle = (workPositionId: string) => {
        setSelectedWorkPositions(prev => 
            prev.includes(workPositionId) 
                ? prev.filter(id => id !== workPositionId)
                : [...prev, workPositionId]
        );
    };

    const handleDownload = () => {
        let allDepartments = false;
        let departmentsToInclude: string[] = [];
        
        switch (departmentScope) {
            case 'single':
                // Scope to the department currently selected on the page (not just the first one).
                departmentsToInclude = currentDepartment ? [String(currentDepartment.id)] : [];
                allDepartments = false;
                break;
            case 'all':
                allDepartments = true;
                break;
            case 'specific':
                departmentsToInclude = selectedDepartments;
                allDepartments = false;
                break;
        }
        
        shiftsReport(startDate, endDate, seasonId, allDepartments, departmentsToInclude, selectedWorkPositions);
    };

    return (
        <AppModal open={open} handleClose={onClose}>
            <Box
                sx={{
                    p: { xs: 3, sm: 5 },
                    background: `linear-gradient(135deg, #f8fafc 0%, #e9ecef 100%)`,
                    width: '100%',
                    minWidth: 320,
                }}
            >
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                    <Box display="flex" alignItems="center" gap={2}>
                        <H4 sx={{ fontWeight: "bold", color: "text.primary" }}>{t('shifts.reportName')}</H4>
                        <BetaChip label="BETA" size="small" />
                    </Box>
                    <Button onClick={onClose} sx={{ minWidth: 'auto', p: 1, fontSize: 20, color: 'grey.600' }}>✕</Button>
                </Box>
                <Box display="flex" flexDirection="column" alignItems="center" mb={4} mt={2}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                        {t('shift.download.departmentScopeLabel')}
                    </Typography>
                    
                    <FormControl component="fieldset" sx={{ width: '100%', maxWidth: 500 }}>
                        <RadioGroup
                            value={departmentScope}
                            onChange={(e) => setDepartmentScope(e.target.value as 'single' | 'all' | 'specific')}
                        >
                            <FormControlLabel
                                value="single"
                                control={<Radio />}
                                label={t('shift.download.currentDepartment')}
                                sx={{ mb: 1 }}
                            />
                            <FormControlLabel
                                value="all"
                                control={<Radio />}
                                label={t('shift.download.allDepartments')}
                                sx={{ mb: 1 }}
                            />
                            <FormControlLabel
                                value="specific"
                                control={<Radio />}
                                label={t('shift.download.specificDepartments')}
                                sx={{ mb: 1 }}
                            />
                        </RadioGroup>
                    </FormControl>
                    
                    {departmentScope === 'specific' && (
                        <Accordion 
                            sx={{ 
                                width: '100%', 
                                maxWidth: 500, 
                                mt: 2,
                                boxShadow: '0 2px 8px rgba(102, 126, 234, 0.06)',
                                borderRadius: 2,
                            }}
                        >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    {t('shift.download.selectDepartments')} ({t('common.tables.select.numSelected', { num: selectedDepartments.length })})
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <FormGroup>
                                    {departments.map((dept) => (
                                        <FormControlLabel
                                            key={dept.id}
                                            control={
                                                <Checkbox
                                                    checked={selectedDepartments.includes(dept.id.toString())}
                                                    onChange={() => handleDepartmentToggle(dept.id.toString())}
                                                />
                                            }
                                            label={dept.name}
                                            sx={{ mb: 0.5 }}
                                        />
                                    ))}
                                </FormGroup>
                            </AccordionDetails>
                        </Accordion>
                    )}
                </Box>
                
                {/* Work Position Selection */}
                <Box display="flex" flexDirection="column" alignItems="center" mb={4}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                        {t('shift.download.workPositionScopeLabel')}
                    </Typography>
                    
                    <Accordion 
                        defaultExpanded={false}
                        sx={{ 
                            width: '100%', 
                            maxWidth: 500,
                            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.06)',
                            borderRadius: 2,
                        }}
                    >
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="subtitle1" fontWeight={600}>
                                {t('shift.download.selectWorkPositions')} ({t('common.tables.select.numSelected', { num: selectedWorkPositions.length })})
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <FormGroup>
                                {workPositions.map((wp) => (
                                    <FormControlLabel
                                        key={wp.id}
                                        control={
                                            <Checkbox
                                                checked={selectedWorkPositions.includes(wp.id)}
                                                onChange={() => handleWorkPositionToggle(wp.id)}
                                            />
                                        }
                                        label={wp.name}
                                        sx={{ mb: 0.5 }}
                                    />
                                ))}
                            </FormGroup>
                        </AccordionDetails>
                    </Accordion>
                </Box>
                <Grid container spacing={3} mb={2}>
                    <Grid item xs={12} sm={6}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                label={t('housing.report.startDate')}
                                value={startDate}
                                onChange={(newValue) => {
                                    if (newValue) setStartDate(newValue);
                                }}
                                format="dd/MM/yyyy"
                                slotProps={{ textField: { fullWidth: true, variant: 'outlined', size: 'small' } }}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                label={t('housing.report.endDate')}
                                value={endDate}
                                onChange={(newValue) => {
                                    if (newValue) setEndDate(newValue);
                                }}
                                format="dd/MM/yyyy"
                                slotProps={{ textField: { fullWidth: true, variant: 'outlined', size: 'small' } }}
                            />
                        </LocalizationProvider>
                    </Grid>
                </Grid>
                <Box display="flex" justifyContent="flex-end" mt={2}>
                    <Button
                        variant="contained"
                        size="large"
                        sx={{ borderRadius: 2, minWidth: 180, fontWeight: 600, fontSize: '1rem', boxShadow: '0 2px 8px rgba(102, 126, 234, 0.10)' }}
                        onClick={handleDownload}
                        disabled={isBefore(endDate, startDate) || (departmentScope === 'specific' && selectedDepartments.length === 0)}
                        endIcon={<Download />}
                    >
                        {t('shift.downloadReport')}
                    </Button>
                </Box>
            </Box>
        </AppModal>
    );
}

export default ShiftReportModal;

